import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { EmailCaptureConfig } from '@/types/vsleads/emailCaptureTypes';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Settings, Clock } from 'lucide-react';

interface TimingTabProps {
  form: UseFormReturn<EmailCaptureConfig>;
}

export function TimingTab({ form }: TimingTabProps) {
  useLanguage();
  const timer = form.watch('timer');
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  
  // Parse current time to minutes and seconds
  const parseTime = (timeStr: string) => {
    const parts = timeStr.split(':');
    return {
      minutes: parseInt(parts[0] || '0'),
      seconds: parseInt(parts[1] || '0')
    };
  };
  
  const { minutes: currentMinutes, seconds: currentSeconds } = parseTime(timer.moment);
  const [selectedMinutes, setSelectedMinutes] = useState(currentMinutes);
  const [selectedSeconds, setSelectedSeconds] = useState(currentSeconds);
  
  const handleTimeSelect = () => {
    const formattedTime = `${String(selectedMinutes).padStart(2, '0')}:${String(selectedSeconds).padStart(2, '0')}`;
    form.setValue('timer.moment', formattedTime, { shouldDirty: true });
    setIsPickerOpen(false);
  };

  return (
    <div className="space-y-6">
      {/* Estratégia de Captura */}
      <Card className="bg-white border border-[#E5E7EB] rounded-xl shadow-[0_2px_6px_rgba(0,0,0,0.04)]">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg transition-colors ${
                timer.enabled 
                  ? 'bg-[#2D72FF]/10' 
                  : 'bg-gray-100'
              }`}>
                <Settings 
                  className="w-6 h-6 transition-colors" 
                  style={{ 
                    color: timer.enabled ? '#2D72FF' : '#9CA3AF'
                  }} 
                />
              </div>
              <div>
                <h3 className="text-[16px] font-semibold text-[#111827]">
                  {translateInterfaceText("Estratégia de Captura")}</h3>
                <p className="text-[13px] text-[#6B7280] mt-0.5">
                  {translateInterfaceText("Ative ou desative o formulário de captura neste vídeo. Quando ativo, o formulário será exibido no momento configurado.")}</p>
              </div>
            </div>
            <Switch
              id="timer-enabled"
              checked={timer.enabled}
              onCheckedChange={(checked) =>
                form.setValue('timer.enabled', checked, { shouldDirty: true })
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Momento */}
      {timer.enabled && (
        <Card className="bg-white border border-[#E2E8F0] rounded-xl shadow-[0_2px_4px_rgba(0,0,0,0.02)]">
          <CardHeader className="pb-4">
            <CardTitle className="text-[17px] font-semibold text-[#101828]">{translateInterfaceText("Momento do Vídeo")}</CardTitle>
            <CardDescription className="text-[13px] text-[#667085]">
              {translateInterfaceText("Defina quando o formulário deve aparecer (formato min:seg)")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Label htmlFor="timer-moment">{translateInterfaceText("Momento (min:seg)")}</Label>
            <div className="flex gap-2 mt-2">
              <Input
                id="timer-moment"
                value={timer.moment}
                onChange={(e) =>
                  form.setValue('timer.moment', e.target.value, { shouldDirty: true })
                }
                placeholder="00:15"
                className="font-mono"
                maxLength={5}
              />
              <Popover open={isPickerOpen} onOpenChange={setIsPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 flex-shrink-0 hover:bg-transparent hover:border-[#E2E8F0]"
                    type="button"
                  >
                    <Clock className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="end">
                  <div className="p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      {/* Minutos */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-[#6B7280]">{translateInterfaceText("Minutos")}</Label>
                        <div className="h-[200px] overflow-y-auto border border-[#E5E7EB] rounded-lg">
                          {Array.from({ length: 60 }, (_, i) => i).map((minute) => (
                            <button
                              key={minute}
                              type="button"
                              onClick={() => setSelectedMinutes(minute)}
                              className={`w-full px-3 py-2 text-sm text-left hover:bg-[#F3F4F6] transition-colors ${
                                selectedMinutes === minute
                                  ? 'bg-[#2D72FF] text-white hover:bg-[#2D72FF]'
                                  : 'text-[#111827]'
                              }`}
                            >
                              {String(minute).padStart(2, '0')}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Segundos */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-[#6B7280]">{translateInterfaceText("Segundos")}</Label>
                        <div className="h-[200px] overflow-y-auto border border-[#E5E7EB] rounded-lg">
                          {Array.from({ length: 60 }, (_, i) => i).map((second) => (
                            <button
                              key={second}
                              type="button"
                              onClick={() => setSelectedSeconds(second)}
                              className={`w-full px-3 py-2 text-sm text-left hover:bg-[#F3F4F6] transition-colors ${
                                selectedSeconds === second
                                  ? 'bg-[#2D72FF] text-white hover:bg-[#2D72FF]'
                                  : 'text-[#111827]'
                              }`}
                            >
                              {String(second).padStart(2, '0')}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-2 border-t border-[#E5E7EB]">
                      <div className="text-sm">
                        <span className="text-[#6B7280]">{translateInterfaceText("Selecionado:")}{" "}</span>
                        <span className="font-mono font-semibold text-[#111827]">
                          {String(selectedMinutes).padStart(2, '0')}:{String(selectedSeconds).padStart(2, '0')}
                        </span>
                      </div>
                      <Button
                        onClick={handleTimeSelect}
                        size="sm"
                        className="bg-[#2D72FF] hover:bg-[#1E5FE6]"
                      >
                        {translateInterfaceText("Aplicar")}</Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
}
